import React, { Fragment, useMemo, forwardRef } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { FormattedMessage } from "react-intl";

import { ModalService } from "../modal";
import { ConfirmationDialog } from "../confirmationDialog/ConfirmationDialog";
import { NotMobile, Mobile } from "../../common/Responsive";
import { Button } from "../button/Button";
import { PauseInternetDropdown } from "./PauseInternetDropdown";
import { PauseInternetModal } from "./PauseInternetModal";
import { usePauseInternet } from "./PauseInternetProvider";
import { humanizePrecisely } from "./pauseInternet.helpers";
import { PauseInternetLabel } from "./PauseInternetLabel";

import "./PauseInternet.scss";

const PauseOptionsTrigger = forwardRef(function PauseOptionsTrigger(
  { label, icon, onClick, size, variant, children, ...props },
  forwardedRef
) {
  return (
    <Fragment>
      {label && <PauseInternetLabel>{label}</PauseInternetLabel>}
      <Button
        ref={forwardedRef}
        className="pause-internet__trigger"
        onClick={onClick}
        color="secondary"
        size={size}
        variant={variant}
        iconRight={icon}
        {...props}
      >
        {children}
      </Button>
    </Fragment>
  );
});

export function PauseInternetOptions({
  onPauseInternet,
  options,
  variant,
  label,
  hiddenLabel,
  size,
}) {
  const pauseOptions = useMemo(
    () =>
      _.map(options, (option, index) => ({
        content: <div>{humanizePrecisely(option.amount, option.units)}</div>,
        action: () => {
          onPauseInternet(option);
        },
        id: `pause-internet-${index}`,
      })),
    [onPauseInternet, options]
  );

  if (pauseOptions.length === 0) {
    return (
      <PauseOptionsTrigger label={label} size={size} variant={variant} disabled>
        <FormattedMessage id="pause_internet" />
      </PauseOptionsTrigger>
    );
  }

  const renderToggle = ({ label, onClick, icon }) => (
    <PauseOptionsTrigger
      label={label}
      onClick={onClick}
      size={size}
      variant={variant}
      icon={icon}
    >
      <FormattedMessage id="pause_internet" />
    </PauseOptionsTrigger>
  );

  return (
    <Fragment>
      <NotMobile>
        <PauseInternetDropdown
          items={pauseOptions}
          label={label}
          hiddenLabel={hiddenLabel}
          renderToggle={renderToggle}
        />
      </NotMobile>
      <Mobile>
        <PauseInternetModal
          items={pauseOptions}
          renderToggle={renderToggle}
          header={<FormattedMessage id="pause_internet" />}
        />
      </Mobile>
    </Fragment>
  );
}

PauseInternetOptions.defaultProps = {
  size: "large",
};

PauseInternetOptions.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      content: PropTypes.node,
      action: PropTypes.func,
    })
  ),
  variant: PropTypes.oneOf(["outlined", "inverted"]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.oneOf(["medium", "large"]),
};

function UnpauseInternetModal({ modal, profileName }) {
  return (
    <ConfirmationDialog
      modal={modal}
      title={<FormattedMessage id="unpause_profile" values={{ profileName }} />}
      content={
        <FormattedMessage id="unpause_modal_text" values={{ profileName }} />
      }
      cancelMessage={<FormattedMessage id="cancel" />}
      submitMessage={<FormattedMessage id="unpause" />}
    />
  );
}

export const useUnpauseCallback = (profileName) => {
  const { unpauseInternet } = usePauseInternet();

  return () => {
    ModalService.show((modal) => ({
      dialog: <UnpauseInternetModal modal={modal} profileName={profileName} />,
    }))
      .then(() => {
        unpauseInternet();
      })
      .catch(() => {});
  };
};
